import { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Menu,
  X,
  Phone,
  Mail,
  MapPin,
  Clock,
  Heart,
  Shield,
  Users,
  Award,
  Calendar as CalendarIcon,
  CheckCircle,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  User,
  LogOut,
  LayoutDashboard,
  Eye,
  EyeOff,
  Trash2,
  Check,
  XCircle,
  AlertCircle,
  Sun,
  Moon,
  Building2,
  Search,
  Plus,
  Image,
  Smile,
  Stethoscope,
  ShieldCheck
} from "lucide-react";
import "@/App.css";
import PixelCard from "./components/PixelCard";
import ProfileCard from "./components/ProfileCard";
import ElectricBorder from "./components/ElectricBorder";
import GooeyNav from "./components/ui/GooeyNav";
import CardSwap, { Card as SwapCard } from "./components/ui/CardSwap";
import BallpitBackground from "@/components/BallpitBackground";
import DiseaseDetailPage from "@/pages/DiseaseDetailPage";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { DISEASE_CATALOG, mergeProblemRow } from "@/data/dentalDiseases";
import { supabaseApi, getStoredAuthUserRole } from "@/lib/supabaseApi";
import { supabase } from "@/lib/supabaseClient";

function getRoleFromStoredToken() {
  // Supabase JWT `role` usually stays `authenticated`; admin should come from
  // our normalized stored user (`users.role` or `user_metadata.role`).
  return getStoredAuthUserRole() || "user";
}

// Helper: get a single string from backend error or network error
function getAuthError(err) {
  if (!err) return "Something went wrong";
  if (err.response?.status === 0 || err.code === "ERR_NETWORK") return "Cannot reach server. Check your Supabase URL and key.";
  const detail = err.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e.msg || JSON.stringify(e)).join(". ") || "Validation error";
  }
  if (typeof detail === "string") return detail;
  if (err.message) return err.message;
  return "Something went wrong";
}

// Logo URL
const LOGO_URL = "/exoi_dental_logo.svg";
const SMILE_REVEAL_STORAGE_KEY = "extol_smile_reveal_complete";

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled) return;
        if (session) {
          try {
            const me = await supabaseApi.getCurrentUser();
            if (cancelled) return;
            setUser(me);
            setToken(session.access_token);
            localStorage.setItem("token", session.access_token);
          } catch (e) {
            console.error("Session restore failed:", e);
            await supabaseApi.logout();
            setUser(null);
            setToken(null);
          }
        } else {
          localStorage.removeItem("token");
          setUser(null);
          setToken(null);
        }
      } catch (e) {
        console.error("getSession failed:", e);
        localStorage.removeItem("token");
        setUser(null);
        setToken(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    checkAuth();

    // Listen for auth state changes (token refresh, sign-out, OAuth redirect, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "SIGNED_IN" && session) {
          localStorage.setItem("token", session.access_token);
          setToken(session.access_token);
          try {
            const me = await supabaseApi.handleOAuthUser();
            setUser(me);
          } catch (e) {
            console.error("Error loading user after sign-in:", e);
          }
        } else if (event === "TOKEN_REFRESHED" && session) {
          localStorage.setItem("token", session.access_token);
          setToken(session.access_token);
        } else if (event === "SIGNED_OUT") {
          localStorage.removeItem("token");
          setUser(null);
          setToken(null);
        }
      }
    );

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    const res = await supabaseApi.login(email, password);
    setToken(res.token);
    setUser(res.user);
    return res;
  };

  const register = async (name, email, phone, password) => {
    const res = await supabaseApi.register(name, email, phone, password);
    setToken(res.token);
    setUser(res.user);
    return res;
  };

  const logout = async () => {
    await supabaseApi.logout();
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 },
  },
};

// Animated section wrapper
const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      transition={{ delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Logo Component
const Logo = ({ className = "" }) => (
  <img
    src={LOGO_URL}
    alt="Extol Dental"
    className={`h-20 w-auto object-contain ${className}`}
  />
);

// Auth Modal Component
const AuthModal = ({ isOpen, onClose, initialMode = "login", onAuthSuccess }) => {
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { login, register } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });

  // Clear error when modal opens so previous errors don't stick
  useEffect(() => {
    if (isOpen) setError("");
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "login") {
        await login(formData.email, formData.password);
      } else {
        await register(formData.name, formData.email, formData.phone, formData.password);
      }
      onClose();
      if (onAuthSuccess) onAuthSuccess();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(getAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px] bg-slate-900 border border-cyan-500/20 text-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold !text-cyan-500">
            {mode === "login" ? "Welcome Back" : "Create Account"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          {mode === "register" && (
            <div>
              <Label className="text-slate-300">Full Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
                data-testid="auth-name-input"
                className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white placeholder:text-slate-500"
                required
              />
            </div>
          )}

          <div>
            <Label className="text-slate-300">Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@example.com"
              data-testid="auth-email-input"
              className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white placeholder:text-slate-500"
              required
            />
          </div>

          {mode === "register" && (
            <div>
              <Label className="text-slate-300">Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+1 (555) 000-0000"
                data-testid="auth-phone-input"
                className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white placeholder:text-slate-500"
                required
              />
            </div>
          )}

          <div>
            <Label className="text-slate-300">Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                data-testid="auth-password-input"
                className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white placeholder:text-slate-500 pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="auth-submit-btn"
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full"
          >
            {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
          </Button>

          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-700" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-slate-900 px-2 text-slate-500">or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            disabled={loading}
            data-testid="google-signin-btn"
            onClick={async () => {
              setLoading(true);
              setError("");
              try {
                await supabaseApi.signInWithGoogle();
              } catch (err) {
                setError(getAuthError(err));
              } finally {
                setLoading(false);
              }
            }}
            className="w-full border-slate-700 text-white hover:bg-slate-800 rounded-full flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Sign in with Google
          </Button>

          <p className="text-center text-slate-400 text-sm">
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
            <button
              type="button"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              className="text-cyan-400 hover:text-cyan-300"
            >
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </form>

      </DialogContent>
    </Dialog>
  );
};

// Header Component
const Header = ({ onHomeClick, onBookClick, onAuthClick, onAdminClick, onMyAppointmentsClick, onClinicsClick, onDoctorsClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const isAdminUser = user?.role === "admin";
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const goToLandingAnchor = (e, hash) => {
    e?.preventDefault?.();
    if (onHomeClick) onHomeClick();

    const targetHash = hash || "#home";

    // If we are not on the landing route, navigate first.
    if (location.pathname !== "/") {
      navigate(`/${targetHash}`);
      // Let the DOM render, then scroll.
      window.setTimeout(() => {
        const el = document.querySelector(targetHash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 80);
      return;
    }

    // Same route: scroll directly.
    const el = document.querySelector(targetHash);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const navItems = [
    {
      label: "Home",
      href: "#home",
      onClick: (e) => goToLandingAnchor(e, "#home"),
    },
    { label: "About", href: "#about", onClick: (e) => goToLandingAnchor(e, "#about") },
    {
      label: "Doctors", href: "#", onClick: () => {
        if (onDoctorsClick) onDoctorsClick();
      }
    },
    { label: "Services", href: "#services", onClick: (e) => goToLandingAnchor(e, "#services") },
    { label: "Plans", href: "#plans", onClick: (e) => goToLandingAnchor(e, "#plans") },
    { label: "Clinics", href: "#", onClick: onClinicsClick },
    { label: "Contact", href: "#contact", onClick: (e) => goToLandingAnchor(e, "#contact") },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-xl border-b border-slate-200/80 ${scrolled ? "shadow-lg shadow-slate-200/60" : "shadow-sm"}`}
    >
      <nav className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo - Extreme Left */}
          <motion.a
            href="#home"
            onClick={(e) => goToLandingAnchor(e, "#home")}
            className="flex items-center gap-2 group flex-shrink-0"
            whileHover={{ scale: 1.05 }}
          >
            <Logo />
          </motion.a>

          {/* Center Navigation */}
          <div className="hidden lg:flex items-center flex-1 justify-center z-10 transition-colors">
            <GooeyNav items={navItems} isDark={false} />
          </div>

          {/* Right Side - Actions */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">

            {user ? (
              <>
                {user.role === "admin" && (
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      onClick={onAdminClick}
                      variant="outline"
                      data-testid="admin-dashboard-btn"
                      className="border-cyan-200 dark:border-cyan-500/30 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-500/10 rounded-full px-3 text-sm transition-colors"
                    >
                      <LayoutDashboard className="w-4 h-4 mr-1" />
                      Admin
                    </Button>
                  </motion.div>
                )}
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    onClick={onMyAppointmentsClick}
                    variant="outline"
                    data-testid="my-appointments-btn"
                    className="border-cyan-200 dark:border-cyan-500/30 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-500/10 rounded-full px-3 text-sm transition-colors"
                  >
                    <CalendarIcon className="w-4 h-4 mr-1" />
                    My Appts
                  </Button>
                </motion.div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-200 dark:border-cyan-500/20 transition-colors">
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={user.name}
                      className="w-6 h-6 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <User className="w-4 h-4 text-cyan-600 dark:text-cyan-400 transition-colors" />
                  )}
                  <span className="text-cyan-700 dark:text-cyan-400 text-sm font-medium transition-colors">{user.name.split(" ")[0]}</span>
                </div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    onClick={logout}
                    variant="ghost"
                    data-testid="logout-btn"
                    className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white p-2 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </motion.div>
              </>
            ) : (
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={onAuthClick}
                  variant="outline"
                  data-testid="login-btn"
                  className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 rounded-full px-4 text-sm"
                >
                  <User className="w-4 h-4 mr-2" />
                  Sign In
                </Button>
              </motion.div>
            )}

            {/* Book Appointment - Extreme Right */}
            {!isAdminUser && (
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={onBookClick}
                  data-testid="header-book-appointment-btn"
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white rounded-full px-6 shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all text-sm"
                >
                  Book Appointment
                </Button>
              </motion.div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden nav-link p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-slate-900/95 backdrop-blur-xl rounded-2xl mb-4 overflow-hidden"
            >
              <div className="p-4 space-y-4">
                {navItems.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="block text-slate-300 hover:text-cyan-400 py-2"
                    onClick={(e) => {
                      setIsMenuOpen(false);
                      if (link.onClick) link.onClick(e);
                    }}
                  >
                    {link.label}
                  </a>
                ))}
                {user ? (
                  <>
                    <div className="flex items-center gap-2 py-2 text-cyan-400">
                      {user.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user.name}
                          className="w-6 h-6 rounded-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <User className="w-4 h-4" />
                      )}
                      <span>{user.name}</span>
                    </div>
                    {user.role === "admin" && (
                      <Button
                        onClick={() => { setIsMenuOpen(false); onAdminClick(); }}
                        variant="outline"
                        className="w-full border-cyan-500/30 text-cyan-400"
                      >
                        Admin Dashboard
                      </Button>
                    )}
                    <Button
                      onClick={() => { setIsMenuOpen(false); onMyAppointmentsClick(); }}
                      variant="outline"
                      className="w-full border-cyan-500/30 text-cyan-400"
                    >
                      My Appointments
                    </Button>
                    <Button
                      onClick={() => { setIsMenuOpen(false); logout(); }}
                      variant="ghost"
                      className="w-full text-slate-400"
                    >
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => { setIsMenuOpen(false); onAuthClick(); }}
                    variant="outline"
                    className="w-full border-cyan-500/30 text-cyan-400"
                  >
                    Sign In
                  </Button>
                )}

                {!isAdminUser && (
                  <Button
                    onClick={() => { setIsMenuOpen(false); onBookClick(); }}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full"
                  >
                    Book Appointment
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};

// Hero Section
const HeroSection = ({ onBookClick }) => {
  const { user } = useAuth();
  const canBook = user?.role !== "admin";
  const defaultStats = [
    { id: "patients", value: "30K+", label: "Happy Patients", icon: Smile },
    { id: "dentists", value: "5+", label: "Dentists", icon: Stethoscope },
    { id: "clinics", value: "2+", label: "Clinics", icon: Building2 },
    { id: "days", value: "7 Days", label: "Open", icon: CalendarIcon },
    { id: "implants", value: "500+", label: "Implants", icon: "https://clovecontent.s3.ap-south-1.amazonaws.com/All/2025/02/Implant-4.svg" },
    { id: "dentures", value: "1K+", label: "Dentures", icon: ShieldCheck },
    { id: "crowns", value: "8K+", label: "Crowns", icon: Award },
  ];
  const [stats, setStats] = useState(defaultStats);
  const [currentSlide, setCurrentSlide] = useState(0);

  const sliderImages = [
    {
      id: 1,
      url: "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?q=80&w=1920&auto=format&fit=crop",
      tag: "Welcome to Excellence in Dental Care",
      title1: "Advanced Dental Care",
      title2: "For a Brighter Smile",
      desc: "Experience world-class dental treatments with our team of expert specialists. We combine cutting-edge technology with compassionate care for your perfect smile."
    },
    {
      id: 2,
      url: "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?q=80&w=1920&auto=format&fit=crop",
      tag: "Trusted by Millions",
      title1: "India's Premier",
      title2: "Dental Network",
      desc: "Join thousands of happy patients who trust our renowned experts for their ongoing oral health and sparkling smiles."
    },
    {
      id: 3,
      url: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?q=80&w=1920&auto=format&fit=crop",
      tag: "State of the Art Facilities",
      title1: "Cutting-Edge",
      title2: "Clinical Excellence",
      desc: "Equipped with the latest in dental technology to ensure painless, quick, and highly effective treatments for every patient."
    }
  ];

  useEffect(() => {
    supabaseApi.getHeroStats().then((data) => {
      if (data?.length) {
        setStats(prevStats =>
          prevStats.map((stat, i) => {
            const fetched = data[i];
            if (fetched) {
              return { ...stat, value: fetched.value, label: fetched.label };
            }
            return stat;
          })
        );
      }
    }).catch(() => { });
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [sliderImages.length]);

  return (
    <section id="home" className="relative min-h-[70vh] sm:min-h-[76vh] flex items-center justify-center pt-20 pb-28 sm:pb-32 overflow-hidden">
      {/* Background Slider */}
      <AnimatePresence initial={false}>
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 0.62, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          className="absolute inset-0 z-0 bg-cover bg-center brightness-[1.08] saturate-[1.25] contrast-[1.04]"
          style={{ backgroundImage: `url(${sliderImages[currentSlide].url})` }}
        />
      </AnimatePresence>

      {/* Light scrims (no hero copy overlay) */}
      <div className="absolute inset-0 z-0 bg-gradient-to-r from-[#0a1628]/55 via-[#0a1628]/25 to-[#0a1628]/15" />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-transparent via-transparent to-[#0a1628]/70 pointer-events-none" />

      <div className="relative z-10 max-w-[1600px] mx-auto px-2 sm:px-4 lg:px-6 py-8 lg:py-10 w-full">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-8 xl:gap-14 items-center">
          <div className="text-center lg:text-left w-full justify-self-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start"
            >
              {canBook && (
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    onClick={onBookClick}
                    data-testid="hero-book-appointment-btn"
                    size="lg"
                    className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white rounded-full px-8 py-5 text-base sm:text-lg shadow-2xl shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all"
                  >
                    <CalendarIcon className="mr-2 h-5 w-5" />
                    Book Appointment
                  </Button>
                </motion.div>
              )}
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="outline"
                  size="lg"
                  data-testid="hero-learn-more-btn"
                  className="border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/20 rounded-full px-8 py-5 text-base sm:text-lg backdrop-blur-md bg-slate-900/30"
                  onClick={() => document.getElementById("about").scrollIntoView({ behavior: "smooth" })}
                >
                  Learn More
                </Button>
              </motion.div>
            </motion.div>

            {/* Slide Indicators */}
            <div className="flex justify-center lg:justify-start gap-2 mt-6">
              {sliderImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`h-2 rounded-full transition-all duration-300 ${idx === currentSlide ? "w-8 bg-cyan-400" : "w-2 bg-slate-600 hover:bg-slate-400"
                    }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="w-full h-full flex justify-center lg:justify-end justify-self-end mt-6 lg:mt-0"
          >
            <ProfileCard
              className="w-full max-w-[360px] xl:max-w-[390px] border-cyan-500/20 bg-transparent"
              avatarUrl="/Doctor.jpeg"
              miniAvatarUrl="/Doctor.jpeg"
              iconUrl="/fire.png"
              behindGlowEnabled={false}
              softPhotoFrame
              name="Dr. Praval Pratap Singh"
              title="Clinical Director, MDS"
              handle="drpravalpratapsingh"
              status="Available for Appointments"
              contactText={canBook ? "Book Now" : "View Profile"}
              onContactClick={canBook ? onBookClick : undefined}
            />
          </motion.div>
        </div>

      </div>

      {/* Photo-Style Stats Strip */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1 }}
        className="absolute inset-x-0 bottom-0 z-20 w-full overflow-hidden border-y border-[#ecdcc7] bg-[#fbf5ec]/95 shadow-[0_-1px_0_rgba(255,255,255,0.7)] backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900/95"
      >
        <div className="h-[3px] w-full bg-gradient-to-r from-[#d9b76b] via-[#f1d79a] to-[#d9b76b]" />
        <div className="mx-auto grid max-w-[1600px] grid-cols-2 gap-x-4 gap-y-4 px-4 py-4 sm:grid-cols-3 sm:px-6 lg:grid-cols-7 lg:gap-x-6 lg:gap-y-0 lg:px-8">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.id || i}
                whileHover={{ y: -1 }}
                className="flex items-center justify-center gap-2.5 whitespace-nowrap rounded-full px-1 py-1 text-center transition-colors lg:justify-start"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center text-[#7b5a40] transition-colors dark:text-slate-400">
                  {typeof Icon === "string" ? (
                    <img
                      src={Icon}
                      alt={`${stat.label} icon`}
                      className="h-5 w-5 sm:h-6 sm:w-6 object-contain"
                      loading="lazy"
                    />
                  ) : (
                    Icon && <Icon className="h-5 w-5 sm:h-6 sm:w-6 stroke-[1.6]" />
                  )}
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-base font-bold text-[#f28c28] sm:text-lg">
                    {stat.value}
                  </span>
                  <span className="text-xs font-semibold text-[#1d2f4f] sm:text-sm dark:text-slate-200">
                    {stat.label}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const scrollToDoctors = useCallback(() => {
    const el = document.getElementById("doctors");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const features = [
    {
      icon: Heart,
      title: "Patient-Centered Care",
      description: "Your comfort and well-being are our top priorities. We listen, understand, and deliver personalized treatments.",
    },
    {
      icon: Shield,
      title: "Advanced Technology",
      description: "State-of-the-art equipment and modern techniques for precise diagnoses and effective treatments.",
    },
    {
      icon: Award,
      title: "Excellence & Quality",
      description: "Award-winning dental practice with a commitment to the highest standards of oral healthcare.",
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Highly trained specialists with years of experience in all aspects of dental care.",
    },
  ];

  return (
    <section id="about" className="relative py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium mb-4">
            About Our Clinic
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Why Choose{" "}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Extol Dental
            </span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            We are committed to providing exceptional dental care with a focus on patient comfort,
            advanced technology, and lasting results.
          </p>
        </AnimatedSection>

        <div className="flex flex-col lg:flex-row items-stretch gap-8 lg:gap-12">
          {/* Left Side: Features Grid (2x2) */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="flex-1 grid sm:grid-cols-2 gap-6"
          >
            {features.map((feature, i) => {
              const isExpert = feature.title === "Expert Team";
              const Wrapper = isExpert ? Link : "div";
              const wrapperProps = isExpert
                ? {
                  to: "/doctors#",
                  onClick: (e) => {
                    e.preventDefault();
                    scrollToDoctors();
                  },
                  className:
                    "block p-6 group cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/70 rounded-2xl h-full",
                }
                : { className: "p-6 group cursor-pointer h-full" };

              return (
                <motion.div key={i} variants={fadeInUp} whileHover={{ y: -10, scale: 1.02 }} className="h-full">
                  <PixelCard
                    variant="blue"
                    gap={8}
                    speed={35}
                    className="w-full h-full rounded-2xl border-cyan-500/20 bg-slate-900/50"
                  >
                    <Wrapper {...wrapperProps}>
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center mb-4 group-hover:from-cyan-500/30 group-hover:to-blue-500/30 transition-all">
                        <feature.icon className="w-7 h-7 text-cyan-400 group-hover:scale-110 transition-transform" />
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-cyan-300 transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-slate-300 text-sm leading-relaxed">{feature.description}</p>
                    </Wrapper>
                  </PixelCard>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Right Side: Photo */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: "-100px" }}
            className="w-full lg:w-5/12 xl:w-1/3 flex-shrink-0"
          >
            <div className="relative h-full min-h-[300px] w-full rounded-3xl overflow-hidden shadow-2xl shadow-cyan-500/10 border border-cyan-500/20 group">
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/10 to-transparent z-10 transition-opacity duration-300 group-hover:opacity-80"></div>
              <img 
                src="/reception.jpg" 
                alt="Clinic Reception" 
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
              />
              <div className="absolute bottom-6 left-6 right-6 z-20">
                <div className="inline-block px-4 py-2 rounded-xl bg-slate-900/80 backdrop-blur-md border border-white/10 text-white font-medium shadow-xl">
                  Modern & Welcoming
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Director Message Block */}
        <AnimatedSection delay={0.2} className="mt-20">
          <div className="relative rounded-3xl overflow-hidden border border-cyan-500/20 bg-white/80 dark:bg-gradient-to-br dark:from-slate-900/80 dark:to-slate-800/60 backdrop-blur-xl shadow-2xl shadow-cyan-500/10">
            {/* Decorative glow */}
            <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />

            <div className="relative flex flex-col lg:flex-row items-center gap-0">
              {/* Left - Director Photo */}
              <div className="flex-shrink-0 w-full lg:w-[340px] xl:w-[380px] flex flex-col items-center justify-center p-8 lg:p-10 lg:border-r border-cyan-500/20">
                <div className="relative">
                  {/* Glowing ring */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-400 via-blue-500 to-cyan-400 opacity-30 blur-md scale-105" />
                  <img
                    src="/director image.jpeg"
                    alt="Director Ruchika Dhakre"
                    className="relative w-56 h-72 xl:w-64 xl:h-80 object-cover rounded-2xl shadow-xl border-2 border-cyan-500/30"
                  />
                  {/* Lanyard badge label overlaid at bottom of image */}
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap border border-white/20">
                    🪪 Director — Extol Dental
                  </div>
                </div>
                <div className="mt-8 text-center">
                  <p className="text-slate-900 dark:text-white font-bold text-xl">Ruchika Dhakre</p>
                  <p className="text-cyan-600 dark:text-cyan-400 text-sm mt-1">Director &amp; Founder</p>
                </div>
              </div>

              {/* Right - Message */}
              <div className="flex-1 p-8 lg:p-12 xl:p-14 flex flex-col justify-center">
                <span className="inline-block px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-600 dark:text-cyan-400 text-xs font-semibold mb-6 tracking-widest uppercase">
                  Message from our Director
                </span>
                <svg className="w-10 h-10 text-cyan-500/50 mb-4" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
                  <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                </svg>
                <p className="text-slate-700 dark:text-slate-200 text-lg lg:text-xl leading-relaxed mb-6 italic font-light">
                  "At Extol Dental, we believe that a healthy smile is not just about teeth — it's about confidence, well-being, and a better quality of life. Our mission has always been to bring world-class dental expertise right to your doorstep, with compassion at the core of everything we do.
                </p>
                <p className="text-slate-600 dark:text-slate-300 text-base lg:text-lg leading-relaxed mb-8">
                  Every patient who walks through our doors is treated like family. We are continuously investing in the latest technology and nurturing a team of passionate professionals so that you receive nothing but the best. I am proud of what we have built — and even more excited about where we are headed together."
                </p>
                <div className="flex items-center gap-4">
                  <div className="h-px flex-1 bg-gradient-to-r from-cyan-500/40 to-transparent" />
                  <p className="text-cyan-600 dark:text-cyan-300 font-semibold text-sm tracking-wide">— Ruchika Dhakre, Director</p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

      </div>
    </section>
  );
};


// Dental Problems Section — cards link to full guides at /services/disease/:slug
const DentalProblemsSection = () => {
  const fallbackProblems = DISEASE_CATALOG.map((c) => ({
    slug: c.slug,
    title: c.title,
    description: c.description,
    details: c.details,
    icon: c.icon,
  }));

  const [problems, setProblems] = useState(fallbackProblems);

  useEffect(() => {
    supabaseApi.getDentalProblems().then((data) => {
      if (data?.length) {
        setProblems(data.map((d) => mergeProblemRow(d)));
      }
    }).catch(() => { });
  }, []);

  return (
    <section id="services" className="relative py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium mb-4">
            Our Services
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Dental Problems{" "}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              We Treat
            </span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Tap a condition to open a full guide with photos, typical pain level, and common symptoms.
          </p>
        </AnimatedSection>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {problems.map((problem, i) => (
            <motion.div key={problem.slug || i} variants={fadeInUp} whileHover={{ y: -5 }}>
              <Link
                to={`/services/disease/${problem.slug}`}
                className="glass-card rounded-2xl overflow-hidden block h-full p-6 cursor-pointer group border border-transparent hover:border-cyan-500/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 transition-all"
                data-testid={`problem-card-${i}`}
              >
                <div className="flex items-start gap-4">
                  <span className="shrink-0 flex items-center justify-center" aria-hidden>
                    {typeof problem.icon === "string" && (/^https?:/i.test(problem.icon) || problem.icon.startsWith("/")) ? (
                      <img
                        src={problem.icon}
                        alt={`${problem.title} icon`}
                        className="h-14 w-14 object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <span className="text-3xl">{problem.icon}</span>
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-semibold text-white group-hover:text-cyan-400 transition-colors">
                      {problem.title}
                    </h3>
                    <p className="text-slate-400 text-sm mt-1 leading-relaxed">{problem.description}</p>
                    <p className="text-slate-500 text-xs mt-3 line-clamp-2">{problem.details}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-cyan-400 mt-4 group-hover:gap-2 transition-all">
                      Read full guide
                      <span aria-hidden>→</span>
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Plans Section
const PlansSection = () => {
  return (
    <section id="plans" className="relative py-24 transition-colors duration-300">
      {/* Dark mode tint so glassmorphism cards have contrast */}
      <div className="absolute inset-0 dark:bg-slate-900/60 pointer-events-none transition-colors" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <AnimatedSection className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-600 dark:text-cyan-400 text-sm font-medium mb-4 transition-colors">
            Our Pricing Plans
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-4 transition-colors">
            Affordable Care{" "}
            <span className="bg-gradient-to-r from-cyan-500 to-blue-500 dark:from-cyan-400 dark:to-blue-400 bg-clip-text text-transparent">
              Packages
            </span>
          </h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto transition-colors">
            Transparent pricing for comprehensive dental care to keep your smile bright and healthy.
          </p>
        </AnimatedSection>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto"
        >
          {/* Standard Plan */}
          <motion.div variants={fadeInUp} whileHover={{ y: -5 }} className="glass-card rounded-3xl p-8 border border-slate-200 dark:border-cyan-500/30 bg-white/90 dark:bg-slate-800/90 backdrop-blur-md relative flex flex-col shadow-lg shadow-cyan-500/5 dark:shadow-cyan-900/20 transition-colors">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2 transition-colors">Standard Plan</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-extrabold text-cyan-500 dark:text-cyan-400 transition-colors">₹999</span>
                <span className="text-lg text-slate-400 dark:text-slate-500 line-through transition-colors">₹4000</span>
              </div>
            </div>
            <ul className="space-y-4 mb-8 text-slate-700 dark:text-slate-300 flex-1 transition-colors">
              <li className="flex items-center gap-3"><CheckCircle className="w-5 h-5 text-cyan-500" /> Consultation</li>
              <li className="flex items-center gap-3"><CheckCircle className="w-5 h-5 text-cyan-500" /> X-Ray</li>
              <li className="flex items-center gap-3"><CheckCircle className="w-5 h-5 text-cyan-500" /> Scaling</li>
              <li className="flex items-center gap-3"><CheckCircle className="w-5 h-5 text-cyan-500" /> 1 Filling</li>
              <li className="flex items-center gap-3"><CheckCircle className="w-5 h-5 text-cyan-500" /> 1 Extraction</li>
            </ul>
            <Button className="w-full bg-cyan-50 dark:bg-cyan-500/10 hover:bg-cyan-500 text-cyan-600 dark:text-cyan-400 hover:text-white border border-cyan-200 dark:border-cyan-500/30 rounded-full py-6 text-lg transition-all mt-6">
              Choose Standard
            </Button>
          </motion.div>

          {/* Premium Plan */}
          <motion.div variants={fadeInUp} whileHover={{ y: -5 }} className="glass-card rounded-3xl p-8 border border-blue-200 dark:border-blue-500/40 bg-gradient-to-br from-blue-50/90 to-cyan-50/90 dark:from-blue-900/80 dark:to-slate-800/90 backdrop-blur-md relative flex flex-col shadow-xl shadow-blue-500/10 overflow-hidden transition-colors">
            <div className="absolute top-5 right-5">
              <span className="bg-gradient-to-r from-blue-500 to-cyan-500 dark:from-blue-500 dark:to-cyan-400 text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-md">Most Popular</span>
            </div>
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2 transition-colors">Premium Plan</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-extrabold text-blue-500 dark:text-blue-400 transition-colors">₹1999</span>
                <span className="text-lg text-slate-400 dark:text-slate-500 line-through transition-colors">₹8000</span>
              </div>
            </div>
            <ul className="space-y-4 mb-8 text-slate-700 dark:text-slate-300 flex-1 transition-colors">
              <li className="flex items-center gap-3 text-cyan-600 dark:text-cyan-300 font-medium transition-colors"><CheckCircle className="w-5 h-5 text-blue-500" /> All features of Standard Plan</li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" />
                <span>1 Root Canal Treatment (RCT)<br /><span className="text-sm text-slate-500 dark:text-slate-400 transition-colors">Painless & Advanced Endodontic Care</span></span>
              </li>
            </ul>
            <Button className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 dark:from-blue-600 dark:to-cyan-500 hover:from-blue-600 hover:to-cyan-600 dark:hover:from-blue-500 dark:hover:to-cyan-400 text-white shadow-lg shadow-blue-500/20 hover:shadow-cyan-500/30 rounded-full py-6 text-lg transition-all mt-6 border-none">
              Choose Premium
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ onBookClick }) => {
  const { user } = useAuth();
  const canBook = user?.role !== "admin";
  return (
    <section className="relative py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <motion.div
            whileHover={{ scale: 1.01 }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 p-12 text-center"
          >
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNDB2NDBoLTQweiIvPjxjaXJjbGUgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIiBjeD0iMjAiIGN5PSIyMCIgcj0iMiIvPjwvZz48L3N2Zz4=')] opacity-30" />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                Ready to Transform Your Smile?
              </h2>
              <p className="text-cyan-100 text-lg max-w-2xl mx-auto mb-8">
                Schedule your appointment today and take the first step towards a healthier,
                more beautiful smile.
              </p>
              {canBook && (
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    onClick={onBookClick}
                    size="lg"
                    data-testid="cta-book-appointment-btn"
                    className="bg-white text-blue-600 hover:bg-cyan-50 rounded-full px-10 py-6 text-lg font-semibold shadow-2xl"
                  >
                    <CalendarIcon className="mr-2 h-5 w-5" />
                    Book Your Appointment
                  </Button>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Contact CTA Section
const ContactCTASection = () => {
  return (
    <section className="relative py-12 pt-0 sm:py-16 sm:pt-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <motion.div
            whileHover={{ scale: 1.01 }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600 p-10 sm:p-14 text-center shadow-[0_20px_60px_-15px_rgba(16,185,129,0.5)] border border-emerald-400/20"
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNDB2NDBoLTQweiIvPjxjaXJjbGUgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIiBjeD0iMjAiIGN5PSIyMCIgcj0iMiIvPjwvZz48L3N2Zz4=')] opacity-30 pointer-events-none" />

            {/* Decorative Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-white/10 rounded-full blur-[80px] pointer-events-none" />

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, type: "spring" }}
              viewport={{ once: true }}
              className="relative z-10 flex flex-col md:flex-row items-center gap-8 md:gap-12"
            >
              {/* QR Code Image - Left Side */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="flex-shrink-0"
              >
                <div className="w-40 h-40 sm:w-52 sm:h-52 rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.3)] border-2 border-white/30 bg-white p-2 transform hover:scale-105 transition-transform duration-300">
                  <img
                    src="/qr-code.jpg"
                    alt="Extol Dental QR Code"
                    className="w-full h-full object-contain rounded-xl"
                  />
                </div>
                <p className="text-emerald-100/80 text-xs sm:text-sm text-center mt-3 font-medium">Scan to visit us</p>
              </motion.div>

              {/* Content - Right Side */}
              <div className="flex flex-col items-center md:items-start text-center md:text-left">
                <div className="relative mb-6 sm:mb-8 group cursor-pointer md:hidden">
                  {/* Glowing ring effect */}
                  <div className="absolute inset-0 bg-white/40 rounded-[2.5rem] blur-2xl group-hover:blur-3xl animate-pulse transition-all duration-500" />
                  <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-[2.5rem] bg-gradient-to-br from-white to-emerald-50 backdrop-blur-xl flex items-center justify-center shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-white/60 transform -rotate-12 group-hover:rotate-0 transition-transform duration-500">
                    <Phone className="w-12 h-12 sm:w-14 sm:h-14 text-emerald-600 drop-shadow-lg" />
                  </div>
                </div>

                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-5 sm:mb-6 tracking-tight">
                  Need Immediate Assistance?
                </h2>
                <p className="text-emerald-50 text-lg sm:text-xl max-w-2xl mb-8 sm:mb-10 font-medium opacity-90">
                  Our dental experts are just a call away. Get in touch with us now for emergency care or any inquiries!
                </p>

                <motion.a
                  href="tel:+919993991888"
                  whileHover={{ scale: 1.05, y: -4 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-4 bg-white text-emerald-700 hover:bg-emerald-50 rounded-full px-8 sm:px-14 py-4 sm:py-6 text-2xl sm:text-4xl font-extrabold shadow-[0_0_40px_rgba(255,255,255,0.3)] hover:shadow-[0_0_60px_rgba(255,255,255,0.6)] transition-all duration-300 ring-4 ring-white/30 hover:ring-white/50"
                >
                  +91 99939 91888
                </motion.a>
              </div>
            </motion.div>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ onDeveloperClick }) => {
  const quickLinks = [
    { name: "Home", href: "#home" },
    { name: "About Us", href: "#about" },
    { name: "Our Doctors", href: "#doctors" },
    { name: "Services", href: "#services" },
    { name: "Developer", href: "#developer" },
    { name: "Contact", href: "#contact" },
  ];

  const fallbackServices = [
    "General Dentistry",
    "Orthodontics",
    "Teeth Whitening",
    "Dental Implants",
    "Root Canal",
    "Periodontics",
  ];

  const [services, setServices] = useState(fallbackServices);
  const [contactInfo, setContactInfo] = useState({
    address: "Surya Mandir, Sagar Paradise, gali, Residency Rd, near Birla hospital",
    city: "Gwalior, Madhya Pradesh 474005",
    phone: "+91 99939 91888",
    email: "care@extoldental.com",
    hours: "Mon-Sat: 9AM-9PM",
  });

  useEffect(() => {
    // Fetch services for footer
    supabaseApi.getServices().then((data) => {
      if (data?.length) {
        setServices(data.map((s) => s.name));
      }
    }).catch(() => { });

    // Fetch site settings for contact info
    supabaseApi.getSiteSettings().then((settings) => {
      if (settings) {
        setContactInfo((prev) => ({
          address: settings.address || prev.address,
          city: settings.city || prev.city,
          phone: settings.phone || prev.phone,
          email: settings.email || prev.email,
          hours: settings.working_hours || settings.hours || prev.hours,
        }));
      }
    }).catch(() => { });
  }, []);

  return (
    <footer id="contact" className="footer-shell relative py-16 border-t border-cyan-500/20 bg-slate-950/75 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-12"
        >
          {/* Brand */}
          <motion.div variants={fadeInUp}>
            <a href="#home" className="flex items-center gap-2 mb-4">
              <Logo />
            </a>
            <p className="text-slate-300 text-sm leading-relaxed mb-6">
              Providing exceptional dental care with cutting-edge technology
              and compassionate service for over 15 years.
            </p>
            <div className="flex gap-3">
              {[Facebook, Twitter, Instagram, Linkedin].map((Icon, i) => (
                <motion.a
                  key={i}
                  href="#"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center text-cyan-400 hover:bg-cyan-500/20 transition-colors"
                >
                  <Icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={fadeInUp}>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-slate-300 hover:text-cyan-300 transition-colors text-sm"
                    onClick={(e) => {
                      if (link.href === "#developer") {
                        e.preventDefault();
                        onDeveloperClick();
                      }
                    }}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div variants={fadeInUp}>
            <h3 className="text-white font-semibold mb-4">Our Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-slate-300 text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div variants={fadeInUp}>
            <h3 className="text-white font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-300 text-sm">
                  {contactInfo.address}
                  <br />
                  {contactInfo.city}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                <a href={`tel:${contactInfo.phone.replace(/[^+\d]/g, '')}`} className="text-slate-300 hover:text-cyan-300 text-sm transition-colors">
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                <a href={`mailto:${contactInfo.email}`} className="text-slate-300 hover:text-cyan-300 text-sm transition-colors">
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                <span className="text-slate-300 text-sm">
                  {contactInfo.hours}
                </span>
              </li>
            </ul>
          </motion.div>
        </motion.div>

        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-12 pt-8 border-t border-cyan-500/10 text-center"
        >
          <p className="text-slate-300/80 text-sm">
            © {new Date().getFullYear()} Extol Dental. All rights reserved.
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

const DeveloperModal = ({ isOpen, onClose }) => {
  const developers = [
    {
      name: "Dhruv Kumar Patel",
      title: "Full-Stack Developer",
      handle: "dhruv.kumar.patel",
      avatar: "/bigger.jpeg",
      email: "dhruvpatel8903@gmail.com",
      color: "#5227FF",
      bio: "Passionate full-stack developer specializing in React, Node.js, and modern web technologies.",
    },
    {
      name: "ShivSagar",
      title: "Developer",
      handle: "shivsagar",
      avatar: "/shivsagar.jpeg",
      email: "",
      color: "#00D4FF",
      bio: "Creative developer focused on building innovative and scalable applications.",
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[720px] max-h-[92vh] overflow-y-auto bg-slate-950 border border-slate-700/50 text-white p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-center bg-gradient-to-r from-violet-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
            Meet the Developers
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4 justify-items-center">
          {developers.map((dev) => (
            <ProfileCard
              key={dev.handle}
              avatarUrl={dev.avatar}
              name={dev.name}
              title={dev.title}
              handle={dev.handle}
              status={dev.bio}
              contactText={dev.email ? "Contact" : ""}
              showUserInfo={true}
              enableTilt={true}
              onContactClick={dev.email ? () => { window.location.href = `mailto:${dev.email}`; } : undefined}
              cardHeight="60svh"
              cardMaxHeight="420px"
              softPhotoFrame={true}
              behindGlowColor={`${dev.color}aa`}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Appointment Modal
const AppointmentModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    patient_name: "",
    email: "",
    phone: "",
    service: "",
    doctor: "",
    notes: "",
  });

  useEffect(() => {
    if (isOpen) {
      fetchServices();
      fetchDoctors();
      if (user) {
        setFormData(prev => ({
          ...prev,
          patient_name: user.name,
          email: user.email,
          phone: user.phone,
        }));
      }
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate]);

  const fetchServices = async () => {
    try {
      const data = await supabaseApi.getServices();
      setServices(data);
    } catch (e) {
      console.error("Error fetching services:", e);
    }
  };

  const fetchDoctors = async () => {
    try {
      const data = await supabaseApi.getDoctors();
      setDoctors(data);
    } catch (e) {
      console.error("Error fetching doctors:", e);
    }
  };

  const fetchAvailableSlots = async (date) => {
    setLoading(true);
    try {
      const dateStr = date.toISOString().split("T")[0];
      const slots = await supabaseApi.getAvailableSlots(dateStr);
      setAvailableSlots(slots);
    } catch (e) {
      console.error("Error fetching slots:", e);
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const dateStr = selectedDate.toISOString().split("T")[0];
      await supabaseApi.createAppointment({
        ...formData,
        date: dateStr,
        time: selectedTime,
      }, null, user);
      setSuccess(true);
    } catch (e) {
      console.error("Error booking appointment:", e);
      alert(e.message || e.response?.data?.detail || "Failed to book appointment");
    }
    setSubmitting(false);
  };

  const resetModal = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedTime("");
    setFormData({
      patient_name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      service: "",
      doctor: "",
      notes: "",
    });
    setSuccess(false);
    onClose();
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today || date.getDay() === 0;
  };

  return (
    <Dialog open={isOpen} onOpenChange={resetModal}>
      <DialogContent className="appointment-modal-theme sm:max-w-[600px] bg-slate-900 border border-cyan-500/20 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            {success ? "Appointment Confirmed!" : "Book Your Appointment"}
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Thank You!</h3>
            <p className="text-slate-400 mb-6">
              Your appointment has been successfully booked.
            </p>
            <Button
              onClick={resetModal}
              data-testid="close-success-modal-btn"
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full px-8"
            >
              Close
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${step >= s
                      ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                      : "bg-slate-700 text-slate-400"
                      }`}
                  >
                    {s}
                  </div>
                  {s < 3 && (
                    <div className={`w-12 h-1 mx-1 transition-all ${step > s ? "bg-cyan-500" : "bg-slate-700"}`} />
                  )}
                </div>
              ))}
            </div>

            {/* Step 1 */}
            {step === 1 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                <h3 className="text-lg font-medium text-white">Select Date & Time</h3>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-1">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={isDateDisabled}
                      className="rounded-xl border border-cyan-500/20 bg-slate-800/50 p-3"
                      data-testid="appointment-calendar"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-slate-300 mb-2 block">Available Time Slots</Label>
                    {loading ? (
                      <div className="text-slate-400 text-center py-8">Loading slots...</div>
                    ) : selectedDate ? (
                      <div className="grid grid-cols-3 gap-2">
                        {availableSlots.length > 0 ? (
                          availableSlots.map((slot) => (
                            <motion.button
                              key={slot}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => setSelectedTime(slot)}
                              data-testid={`time-slot-${slot}`}
                              className={`p-2 rounded-lg text-sm font-medium transition-all ${selectedTime === slot
                                ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                                : "bg-slate-700/50 text-slate-300 hover:bg-slate-600/50"
                                }`}
                            >
                              {slot}
                            </motion.button>
                          ))
                        ) : (
                          <p className="col-span-3 text-slate-400 text-center py-4">No slots available</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-center py-8">Select a date to see available slots</p>
                    )}
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!selectedDate || !selectedTime}
                    data-testid="step1-next-btn"
                    className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full px-8"
                  >
                    Next
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                <h3 className="text-lg font-medium text-white">Select Service & Doctor</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-300 mb-2 block">Service</Label>
                    <Select value={formData.service} onValueChange={(v) => setFormData({ ...formData, service: v })}>
                      <SelectTrigger className="bg-slate-800/50 border-cyan-500/20 text-white" data-testid="service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-cyan-500/20">
                        {services.map((s) => (
                          <SelectItem key={s.id} value={String(s.id)} className="text-white hover:bg-slate-700">
                            {s.name} - ${s.price}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300 mb-2 block">Preferred Doctor (Optional)</Label>
                    <Select value={formData.doctor} onValueChange={(v) => setFormData({ ...formData, doctor: v })}>
                      <SelectTrigger className="bg-slate-800/50 border-cyan-500/20 text-white" data-testid="doctor-select">
                        <SelectValue placeholder="Any available doctor" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-cyan-500/20">
                        {doctors.map((d) => (
                          <SelectItem key={d.id} value={String(d.id)} className="text-white hover:bg-slate-700">
                            {d.name} - {d.specialty}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 rounded-full"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    disabled={!formData.service}
                    data-testid="step2-next-btn"
                    className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full px-8"
                  >
                    Next
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                <h3 className="text-lg font-medium text-white">Your Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300 mb-2 block">Full Name</Label>
                    <Input
                      value={formData.patient_name}
                      onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                      placeholder="John Doe"
                      data-testid="patient-name-input"
                      className="bg-slate-800/50 border-cyan-500/20 text-white placeholder:text-slate-500"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 mb-2 block">Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      data-testid="patient-email-input"
                      className="bg-slate-800/50 border-cyan-500/20 text-white placeholder:text-slate-500"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 mb-2 block">Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 (555) 000-0000"
                      data-testid="patient-phone-input"
                      className="bg-slate-800/50 border-cyan-500/20 text-white placeholder:text-slate-500"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 mb-2 block">Notes (Optional)</Label>
                    <Input
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Any special requirements"
                      className="bg-slate-800/50 border-cyan-500/20 text-white placeholder:text-slate-500"
                    />
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-xl p-4 border border-cyan-500/20">
                  <h4 className="text-sm font-medium text-cyan-400 mb-2">Appointment Summary</h4>
                  <div className="text-sm text-slate-300 space-y-1">
                    <p>Date: {selectedDate?.toLocaleDateString()}</p>
                    <p>Time: {selectedTime}</p>
                    <p>Service: {services.find((s) => String(s.id) === formData.service)?.name}</p>
                    {formData.doctor && <p>Doctor: {doctors.find((d) => String(d.id) === formData.doctor)?.name}</p>}
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 rounded-full"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!formData.patient_name || !formData.email || !formData.phone || submitting}
                    data-testid="submit-appointment-btn"
                    className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full px-8"
                  >
                    {submitting ? "Booking..." : "Confirm Appointment"}
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// My Appointments Modal
const MyAppointmentsModal = ({ isOpen, onClose }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token, user } = useAuth();
  const { isDark } = useTheme();

  const fetchMyAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await supabaseApi.getMyAppointments(null, user);
      setAppointments(data);
    } catch (e) {
      console.error("Error fetching appointments:", e);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (isOpen && token) {
      fetchMyAppointments();
    }
  }, [isOpen, token, fetchMyAppointments]);

  const handleCancel = async (id) => {
    if (!window.confirm("Are you sure you want to cancel this appointment?")) return;
    try {
      await supabaseApi.cancelAppointment(id);
      fetchMyAppointments();
    } catch (e) {
      console.error("Error cancelling:", e);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      confirmed: "bg-green-500/20 text-green-400 border-green-500/30",
      completed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
    };
    return <Badge className={`${styles[status] || styles.pending} border`}>{status}</Badge>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`my-appointments-modal-theme sm:max-w-[700px] max-h-[90vh] overflow-y-auto ${isDark
          ? "bg-slate-900 border border-cyan-500/20 text-white"
          : "bg-white border border-slate-300 text-slate-900"
          }`}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            My Appointments
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8 text-slate-400">Loading...</div>
        ) : appointments.length === 0 ? (
          <div className="text-center py-8">
            <CalendarIcon className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No appointments found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {appointments.map((apt) => (
              <div key={apt.id} className="glass-card rounded-xl p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-white font-medium">{apt.service}</span>
                      {getStatusBadge(apt.status)}
                    </div>
                    <p className="text-slate-400 text-sm">
                      {new Date(apt.date).toLocaleDateString()} at {apt.time}
                    </p>
                    {apt.doctor && <p className="text-cyan-400 text-sm">{apt.doctor}</p>}
                  </div>
                  {apt.status === "pending" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancel(apt.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Doctors Directory Page
const fallbackDoctors = [
  {
    id: 1,
    name: "Dr. Praval Pratap Singh",
    specialty: "Specialization details coming soon",
    bio: "Detailed profile will be updated once specialization and experience details are provided.",
    experience: "Experience details coming soon",
    image_url: "https://i.pravatar.cc/400?img=11"
  },
  {
    id: 2,
    name: "Dr. Akash Sharma",
    specialty: "Specialization details coming soon",
    bio: "Detailed profile will be updated once specialization and experience details are provided.",
    experience: "Experience details coming soon",
    image_url: "https://i.pravatar.cc/400?img=12"
  },
  {
    id: 3,
    name: "Dr. Tarun Nagpal",
    specialty: "Specialization details coming soon",
    bio: "Detailed profile will be updated once specialization and experience details are provided.",
    experience: "Experience details coming soon",
    image_url: "https://i.pravatar.cc/400?img=13"
  },
  {
    id: 4,
    name: "Dr. Dheeraj Sharma",
    specialty: "Specialization details coming soon",
    bio: "Detailed profile will be updated once specialization and experience details are provided.",
    experience: "Experience details coming soon",
    image_url: "https://i.pravatar.cc/400?img=14"
  },
  {
    id: 5,
    name: "Dr. Ayushi Agrawal",
    specialty: "Specialization details coming soon",
    bio: "Detailed profile will be updated once specialization and experience details are provided.",
    experience: "Experience details coming soon",
    image_url: "https://i.pravatar.cc/400?img=5"
  }
];

// Doctors Directory Page
const DoctorsDirectoryPage = ({ onBookClick }) => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const { user } = useAuth();
  const canBook = user?.role !== "admin";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    setLoading(true);
    try {
      // For now, keep the directory focused on the five curated doctors.
      setDoctors(fallbackDoctors);
    } catch (e) {
      console.error("Error fetching doctors:", e);
      setDoctors(fallbackDoctors);
    }
    setLoading(false);
  };

  const filteredDoctors = doctors.filter((doc) =>
    (doc.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (doc.specialty || "").toLowerCase().includes(search.toLowerCase())
  );

  const activeDoc = filteredDoctors[activeIndex] || filteredDoctors[0];

  return (
    <div className="doctors-page-shell pt-32 pb-20 min-h-screen text-slate-900 dark:text-white flex flex-col items-center relative z-20 transition-colors duration-300">
      <div className="max-w-7xl w-full px-4 sm:px-6 lg:px-8 flex-1 flex flex-col">
        <div className="doctors-toolbar p-6 border border-slate-200 dark:border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 bg-white/50 dark:bg-slate-900/50 rounded-3xl backdrop-blur-md relative z-10 transition-colors">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 dark:from-cyan-400 dark:to-blue-400 bg-clip-text text-transparent transition-colors">
              Our Expert Doctors
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-2 transition-colors">Found {filteredDoctors.length} specialists</p>
          </div>
          <div className="relative max-w-sm w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 w-5 h-5 transition-colors" />
            <Input
              placeholder="Search doctors by name or specialty..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setActiveIndex(0);
              }}
              className="doctors-search-input pl-12 bg-white/80 dark:bg-slate-900/80 border-slate-300 dark:border-cyan-500/20 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 h-12 w-full rounded-full text-lg shadow-inner transition-colors"
            />
          </div>
        </div>

        <div className="flex-1 w-full flex items-center justify-center">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-cyan-600 dark:text-cyan-400 transition-colors">Loading specialists...</p>
            </div>
          ) : filteredDoctors.length === 0 ? (
            <div className="doctors-empty text-center py-20 text-slate-600 dark:text-slate-400 transition-colors">
              <p className="text-lg">No doctors found matching "{search}"</p>
              <Button
                variant="outline"
                className="mt-6 border-cyan-500/30 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/30 rounded-full px-8 transition-colors"
                onClick={() => setSearch("")}
              >
                Clear Search
              </Button>
            </div>
          ) : (
            <div className="w-full flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              {/* Left Side: Doctor Details */}
              <motion.div
                key={activeDoc?.id || activeIndex}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="flex-1 space-y-6 z-10 w-full"
              >
                {activeDoc && (
                  <>
                    <h2 className="text-4xl lg:text-5xl lg:leading-tight font-extrabold text-slate-900 dark:text-white mt-4 transition-colors">
                      {activeDoc.name}
                    </h2>
                    <p className="text-2xl text-cyan-600 dark:text-cyan-400 font-medium transition-colors">
                      {activeDoc.specialty}
                    </p>

                    <div className="doctor-detail-chip flex items-center gap-3 text-slate-700 dark:text-slate-100 bg-white/80 dark:bg-slate-800/90 w-fit px-5 py-2.5 rounded-full border border-slate-200 dark:border-cyan-500/20 shadow dark:shadow-none transition-colors">
                      <Award className="w-5 h-5 text-cyan-500 dark:text-cyan-400" />
                      <span className="font-medium">{activeDoc.experience || "10+ Years Experience"}</span>
                    </div>

                    <p className="doctor-detail-text text-slate-600 dark:text-slate-400 text-lg leading-relaxed max-w-xl transition-colors">
                      {activeDoc.bio || "A dedicated and highly skilled dental professional committed to providing exceptional, patient-centered care. Specializes in advanced treatments and preventive dentistry to ensure every patient leaves with a healthy, confident smile."}
                    </p>

                    {canBook && (
                      <div className="pt-8">
                        <Button
                          onClick={() => onBookClick()}
                          className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white rounded-full px-8 py-6 text-lg shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)] transition-all flex items-center"
                        >
                          <CalendarIcon className="w-5 h-5 mr-3" />
                          Book Appointment with {activeDoc.name.split(' ')[0]}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </motion.div>

              {/* Right Side: CardSwap Photos */}
              <div className="flex-1 w-full lg:w-1/2 flex justify-center lg:justify-center items-center relative mt-16 pb-20 lg:mt-0 xl:mr-10">
                <CardSwap
                  onActiveChange={setActiveIndex}
                  width={340}
                  height={420}
                  delay={5000}
                >
                  {filteredDoctors.map((doc, idx) => (
                    <SwapCard key={doc.id || idx}>
                      <img
                        src={doc.image || doc.image_url || "/doctor.png"}
                        alt={doc.name}
                        className="w-full h-full object-cover rounded-xl border border-slate-200 dark:border-white/10"
                      />
                      <div className="absolute inset-x-0 bottom-0 py-6 text-center rounded-b-xl" style={{ backgroundImage: "linear-gradient(to top, rgba(0,0,0,0.95), transparent)" }}>
                        <p className="text-white font-semibold text-lg drop-shadow-md">{doc.name}</p>
                      </div>
                    </SwapCard>
                  ))}
                </CardSwap>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Clinics Finder Modal
const ClinicsModal = ({ isOpen, onClose }) => {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [activeTab, setActiveTab] = useState("find");

  useEffect(() => {
    if (isOpen) {
      fetchClinics();
    }
  }, [isOpen]);

  const fetchClinics = async () => {
    setLoading(true);
    try {
      const data = await supabaseApi.getClinics();
      setClinics(data || []);
      if (data?.length) {
        setSelectedClinic(data[0]);
      } else {
        setSelectedClinic(null);
      }
      setActiveTab("find");
    } catch (e) {
      console.error("Error fetching clinics:", e);
      setClinics([]);
      setSelectedClinic(null);
    }
    setLoading(false);
  };

  const normalizedSearch = search.trim().toLowerCase();
  const filteredClinics = clinics.filter((clinic) => {
    if (!normalizedSearch) return true;
    const haystack = `${clinic.name} ${clinic.address} ${clinic.city} ${clinic.state} ${clinic.zip_code}`.toLowerCase();
    return haystack.includes(normalizedSearch);
  });

  const openClinicDetails = (clinic) => {
    setSelectedClinic(clinic);
    setActiveTab("details");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="clinics-modal-theme sm:max-w-[900px] bg-slate-900 border border-cyan-500/20 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            Find a Clinic
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 bg-slate-800/60">
            <TabsTrigger value="find">Find Clinic</TabsTrigger>
            <TabsTrigger value="details" disabled={!selectedClinic}>Clinic Details</TabsTrigger>
          </TabsList>

          <TabsContent value="find" className="space-y-4 mt-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by clinic, city, state, or ZIP"
                data-testid="clinic-search-input"
                className="pl-9 bg-slate-800/50 border-cyan-500/20 text-white"
              />
            </div>

            {loading ? (
              <p className="text-slate-400 py-6 text-center">Loading clinics...</p>
            ) : filteredClinics.length === 0 ? (
              <p className="text-slate-400 py-6 text-center">No clinics found</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredClinics.map((clinic) => (
                  <Card key={clinic.id} className="bg-slate-800/40 border-cyan-500/10">
                    <CardContent className="p-4 space-y-3">
                      <div>
                        <h4 className="text-white font-semibold">{clinic.name}</h4>
                        <p className="text-slate-400 text-sm flex items-start gap-2 mt-1">
                          <MapPin className="w-4 h-4 mt-0.5 text-cyan-400" />
                          <span>{clinic.address}, {clinic.city}, {clinic.state} {clinic.zip_code}</span>
                        </p>
                      </div>
                      <p className="text-slate-300 text-sm flex items-center gap-2">
                        <Clock className="w-4 h-4 text-cyan-400" />
                        {clinic.working_hours || "Timings unavailable"}
                      </p>
                      <Button
                        onClick={() => openClinicDetails(clinic)}
                        className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full"
                        data-testid={`clinic-view-details-${clinic.id}`}
                      >
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="details" className="mt-4">
            {!selectedClinic ? (
              <p className="text-slate-400 text-center py-8">Select a clinic from Find Clinic tab</p>
            ) : (
              <Card className="bg-slate-800/40 border-cyan-500/10">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-xl text-white font-semibold">{selectedClinic.name}</h3>
                    <Badge className={selectedClinic.is_active ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                      {selectedClinic.is_active ? "Open" : "Inactive"}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <p className="text-slate-300 flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-0.5 text-cyan-400" />
                      <span>{selectedClinic.address}, {selectedClinic.city}, {selectedClinic.state} {selectedClinic.zip_code}</span>
                    </p>
                    <p className="text-slate-300 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-cyan-400" />
                      {selectedClinic.phone}
                    </p>
                    <p className="text-slate-300 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-cyan-400" />
                      {selectedClinic.email}
                    </p>
                    <p className="text-slate-300 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-cyan-400" />
                      {selectedClinic.working_hours || "Timings unavailable"}
                    </p>
                  </div>

                  {selectedClinic.services?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-cyan-400 text-sm font-medium">Available Services</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedClinic.services.map((service) => (
                          <Badge key={service} className="bg-cyan-500/15 text-cyan-300 border border-cyan-500/20">
                            {service}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setActiveTab("find")}
                      className="border-cyan-500/30 text-cyan-400 rounded-full"
                    >
                      Back To Clinic Search
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// Admin Dashboard
const AdminDashboard = ({ isOpen, onClose }) => {
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [users, setUsers] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [appointmentSearch, setAppointmentSearch] = useState("");
  const [showAddClinic, setShowAddClinic] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [newClinic, setNewClinic] = useState({
    name: "", address: "", city: "", state: "", zip_code: "",
    phone: "", email: "", image_url: "", working_hours: "Mon-Sat: 9AM-9PM",
    services: []
  });
  const { token, user } = useAuth();

  useEffect(() => {
    if (isOpen && token) {
      fetchData();
    }
  }, [isOpen, token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, aptsRes, usersRes, clinicsRes] = await Promise.allSettled([
        supabaseApi.getAdminStats(),
        supabaseApi.getAdminAppointments(),
        supabaseApi.getAdminUsers(),
        supabaseApi.getAdminClinics(),
      ]);
      setStats(statsRes.status === "fulfilled" ? statsRes.value : null);
      setAppointments(aptsRes.status === "fulfilled" ? aptsRes.value : []);
      setUsers(usersRes.status === "fulfilled" ? usersRes.value : []);
      setClinics(clinicsRes.status === "fulfilled" ? clinicsRes.value : []);
      [statsRes, aptsRes, usersRes, clinicsRes].forEach((r, i) => {
        if (r.status === "rejected") {
          console.error(`Admin fetch failed [${i}]:`, r.reason);
        }
      });
    } catch (e) {
      console.error("Error fetching admin data:", e);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await supabaseApi.updateAppointmentStatus(id, status);
      fetchData();
    } catch (e) {
      console.error("Error updating status:", e);
    }
  };

  const deleteAppointment = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    try {
      await supabaseApi.deleteAppointment(id);
      fetchData();
    } catch (e) {
      console.error("Error deleting:", e);
    }
  };

  const addClinic = async () => {
    try {
      await supabaseApi.addClinic(newClinic);
      setShowAddClinic(false);
      setNewClinic({
        name: "", address: "", city: "", state: "", zip_code: "",
        phone: "", email: "", image_url: "", working_hours: "Mon-Sat: 9AM-9PM",
        services: []
      });
      fetchData();
    } catch (e) {
      console.error("Error adding clinic:", e);
      alert(e.message || e.response?.data?.detail || "Failed to add clinic");
    }
  };

  const deleteClinic = async (id) => {
    if (!window.confirm("Delete this clinic?")) return;
    try {
      await supabaseApi.deleteClinic(id);
      fetchData();
    } catch (e) {
      console.error("Error deleting clinic:", e);
    }
  };

  const filteredAppointments = appointments
    .filter((a) => (statusFilter === "all" ? true : a.status === statusFilter))
    .filter((a) => {
      const q = appointmentSearch.trim().toLowerCase();
      if (!q) return true;
      const haystack = `${a.patient_name} ${a.email} ${a.phone} ${a.service} ${a.date} ${a.time} ${a.status}`.toLowerCase();
      return haystack.includes(q);
    });

  const changeAdminPassword = async () => {
    setPasswordMessage("");
    setPasswordError("");
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      setPasswordError("Please fill all password fields.");
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError("New password and confirm password do not match.");
      return;
    }
    setPasswordLoading(true);
    try {
      const res = await supabaseApi.changePassword(
        null,
        user?.email,
        passwordForm.current_password,
        passwordForm.new_password
      );
      setPasswordMessage(res?.message || "Password updated successfully.");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) {
      setPasswordError(e.message || e.response?.data?.detail || "Failed to change password.");
    }
    setPasswordLoading(false);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      confirmed: "bg-green-500/20 text-green-400 border-green-500/30",
      completed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
    };
    return <Badge className={`${styles[status] || styles.pending} border`}>{status}</Badge>;
  };

  if (!isOpen) return null;

  return (
    <div className="admin-dashboard-theme fixed inset-0 z-50 bg-slate-900 overflow-auto">
      <div className="min-h-screen">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-xl border-b border-cyan-500/10 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Logo />
              <div>
                <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-slate-400 text-sm">Manage appointments and users</p>
              </div>
            </div>
            <Button
              onClick={onClose}
              variant="outline"
              data-testid="close-admin-btn"
              className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 rounded-full"
            >
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          {loading ? (
            <div className="text-center py-20 text-slate-400">Loading...</div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
                {[
                  { label: "Total", value: stats?.total_appointments || 0, icon: CalendarIcon, color: "cyan" },
                  { label: "Today", value: stats?.today_appointments || 0, icon: Clock, color: "blue" },
                  { label: "Pending", value: stats?.pending || 0, icon: AlertCircle, color: "yellow" },
                  { label: "Confirmed", value: stats?.confirmed || 0, icon: Check, color: "green" },
                  { label: "Completed", value: stats?.completed || 0, icon: CheckCircle, color: "blue" },
                  { label: "Cancelled", value: stats?.cancelled || 0, icon: XCircle, color: "red" },
                  { label: "Users", value: stats?.total_users || 0, icon: Users, color: "purple" },
                  { label: "Clinics", value: stats?.total_clinics || 0, icon: Building2, color: "cyan" },
                ].map((stat, i) => (
                  <Card key={i} className="bg-slate-800/50 border-cyan-500/10">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color === "cyan"
                            ? "bg-cyan-500/20"
                            : stat.color === "blue"
                              ? "bg-blue-500/20"
                              : stat.color === "yellow"
                                ? "bg-yellow-500/20"
                                : stat.color === "green"
                                  ? "bg-green-500/20"
                                  : stat.color === "red"
                                    ? "bg-red-500/20"
                                    : "bg-purple-500/20"
                            }`}
                        >
                          <stat.icon
                            className={`w-5 h-5 ${stat.color === "cyan"
                              ? "text-cyan-400"
                              : stat.color === "blue"
                                ? "text-blue-400"
                                : stat.color === "yellow"
                                  ? "text-yellow-400"
                                  : stat.color === "green"
                                    ? "text-green-400"
                                    : stat.color === "red"
                                      ? "text-red-400"
                                      : "text-purple-400"
                              }`}
                          />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-white">{stat.value}</p>
                          <p className="text-slate-400 text-xs">{stat.label}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Tabs defaultValue="appointments" className="space-y-6">
                <TabsList className="bg-slate-800/50 border border-cyan-500/10">
                  <TabsTrigger value="appointments" className="data-[state=active]:bg-cyan-500/20">
                    Appointments
                  </TabsTrigger>
                  <TabsTrigger value="clinics" className="data-[state=active]:bg-cyan-500/20">
                    Clinics
                  </TabsTrigger>
                  <TabsTrigger value="users" className="data-[state=active]:bg-cyan-500/20">
                    Users
                  </TabsTrigger>
                  <TabsTrigger value="settings" className="data-[state=active]:bg-cyan-500/20">
                    Settings
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="appointments">
                  <Card className="bg-slate-800/50 border-cyan-500/10">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="text-white">Appointments</CardTitle>
                      <div className="flex items-center gap-2">
                        <Input
                          value={appointmentSearch}
                          onChange={(e) => setAppointmentSearch(e.target.value)}
                          placeholder="Search appointments..."
                          className="w-56 bg-slate-700/50 border-cyan-500/20 text-white"
                        />
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                          <SelectTrigger className="w-40 bg-slate-700/50 border-cyan-500/20 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-cyan-500/20">
                            <SelectItem value="all" className="text-white">All Status</SelectItem>
                            <SelectItem value="pending" className="text-white">Pending</SelectItem>
                            <SelectItem value="confirmed" className="text-white">Confirmed</SelectItem>
                            <SelectItem value="completed" className="text-white">Completed</SelectItem>
                            <SelectItem value="cancelled" className="text-white">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-cyan-500/10">
                              <TableHead className="text-slate-400">Patient</TableHead>
                              <TableHead className="text-slate-400">Contact</TableHead>
                              <TableHead className="text-slate-400">Service</TableHead>
                              <TableHead className="text-slate-400">Date & Time</TableHead>
                              <TableHead className="text-slate-400">Status</TableHead>
                              <TableHead className="text-slate-400">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredAppointments.map((apt) => (
                              <TableRow key={apt.id} className="border-cyan-500/10">
                                <TableCell>
                                  <div>
                                    <p className="text-white font-medium">{apt.patient_name}</p>
                                    <p className="text-slate-400 text-xs">{apt.email}</p>
                                  </div>
                                </TableCell>
                                <TableCell className="text-slate-300">{apt.phone || "-"}</TableCell>
                                <TableCell className="text-slate-300">{apt.service}</TableCell>
                                <TableCell className="text-slate-300">{apt.date} {apt.time}</TableCell>
                                <TableCell>{getStatusBadge(apt.status)}</TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    {apt.status === "pending" && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => updateStatus(apt.id, "confirmed")}
                                        className="text-green-400 hover:bg-green-500/10 p-1 h-8 w-8"
                                        data-testid={`confirm-apt-${apt.id}`}
                                      >
                                        <Check className="w-4 h-4" />
                                      </Button>
                                    )}
                                    {apt.status === "confirmed" && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => updateStatus(apt.id, "completed")}
                                        className="text-blue-400 hover:bg-blue-500/10 p-1 h-8 w-8"
                                      >
                                        <CheckCircle className="w-4 h-4" />
                                      </Button>
                                    )}
                                    {apt.status !== "cancelled" && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => updateStatus(apt.id, "cancelled")}
                                        className="text-yellow-400 hover:bg-yellow-500/10 p-1 h-8 w-8"
                                      >
                                        <XCircle className="w-4 h-4" />
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => deleteAppointment(apt.id)}
                                      className="text-red-400 hover:bg-red-500/10 p-1 h-8 w-8"
                                      data-testid={`delete-apt-${apt.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      {filteredAppointments.length === 0 && (
                        <p className="text-slate-400 text-sm text-center py-4">
                          No appointments match the selected filter/search.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="clinics">
                  <Card className="bg-slate-800/50 border-cyan-500/10">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="text-white">Manage Clinics</CardTitle>
                      <Button
                        onClick={() => setShowAddClinic(true)}
                        data-testid="add-clinic-btn"
                        className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Clinic
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {/* Add Clinic Form */}
                      {showAddClinic && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          className="mb-6 p-4 bg-slate-700/50 rounded-xl border border-cyan-500/20"
                        >
                          <h4 className="text-white font-medium mb-4">Add New Clinic</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                              <Label className="text-slate-300">Clinic Name</Label>
                              <Input
                                value={newClinic.name}
                                onChange={(e) => setNewClinic({ ...newClinic, name: e.target.value })}
                                placeholder="Extol Dental - Downtown"
                                data-testid="clinic-name-input"
                                className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-300">Address</Label>
                              <Input
                                value={newClinic.address}
                                onChange={(e) => setNewClinic({ ...newClinic, address: e.target.value })}
                                placeholder="123 Main Street"
                                className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-300">City</Label>
                              <Input
                                value={newClinic.city}
                                onChange={(e) => setNewClinic({ ...newClinic, city: e.target.value })}
                                placeholder="San Francisco"
                                className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-300">State</Label>
                              <Input
                                value={newClinic.state}
                                onChange={(e) => setNewClinic({ ...newClinic, state: e.target.value })}
                                placeholder="CA"
                                className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-300">ZIP Code</Label>
                              <Input
                                value={newClinic.zip_code}
                                onChange={(e) => setNewClinic({ ...newClinic, zip_code: e.target.value })}
                                placeholder="94102"
                                className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-300">Phone</Label>
                              <Input
                                value={newClinic.phone}
                                onChange={(e) => setNewClinic({ ...newClinic, phone: e.target.value })}
                                placeholder="+1 (415) 555-1234"
                                className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-300">Email</Label>
                              <Input
                                value={newClinic.email}
                                onChange={(e) => setNewClinic({ ...newClinic, email: e.target.value })}
                                placeholder="clinic@extoldental.com"
                                className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-300">Working Hours</Label>
                              <Input
                                value={newClinic.working_hours}
                                onChange={(e) => setNewClinic({ ...newClinic, working_hours: e.target.value })}
                                placeholder="Mon-Sat: 9AM-9PM"
                                className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white"
                              />
                            </div>
                            <div className="md:col-span-2 lg:col-span-1">
                              <Label className="text-slate-300">Image URL</Label>
                              <Input
                                value={newClinic.image_url}
                                onChange={(e) => setNewClinic({ ...newClinic, image_url: e.target.value })}
                                placeholder="https://example.com/image.jpg"
                                data-testid="clinic-image-input"
                                className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 mt-4">
                            <Button
                              onClick={addClinic}
                              data-testid="save-clinic-btn"
                              disabled={!newClinic.name || !newClinic.email || !newClinic.city}
                              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full"
                            >
                              Save Clinic
                            </Button>
                            <Button
                              onClick={() => setShowAddClinic(false)}
                              variant="outline"
                              className="border-cyan-500/30 text-cyan-400 rounded-full"
                            >
                              Cancel
                            </Button>
                          </div>
                        </motion.div>
                      )}

                      {/* Clinics List */}
                      <div className="grid gap-4">
                        {clinics.map((clinic) => (
                          <div
                            key={clinic.id}
                            className="flex gap-4 p-4 bg-slate-700/30 rounded-xl border border-cyan-500/10"
                          >
                            {clinic.image_url ? (
                              <img
                                src={clinic.image_url}
                                alt={clinic.name}
                                className="w-32 h-24 object-cover rounded-lg"
                              />
                            ) : (
                              <div className="w-32 h-24 bg-slate-600/50 rounded-lg flex items-center justify-center">
                                <Image className="w-8 h-8 text-slate-500" />
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="text-white font-medium">{clinic.name}</h4>
                                  <p className="text-slate-400 text-sm">{clinic.address}, {clinic.city}, {clinic.state} {clinic.zip_code}</p>
                                  <p className="text-cyan-400 text-sm">{clinic.phone}</p>
                                  <p className="text-slate-500 text-xs mt-1">{clinic.working_hours}</p>
                                </div>
                                <div className="flex gap-2">
                                  <Badge className={clinic.is_active ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                                    {clinic.is_active ? "Active" : "Inactive"}
                                  </Badge>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteClinic(clinic.id)}
                                    className="text-red-400 hover:bg-red-500/10 p-1 h-8 w-8"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {clinics.length === 0 && (
                          <p className="text-slate-400 text-center py-8">No clinics added yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="users">
                  <Card className="bg-slate-800/50 border-cyan-500/10">
                    <CardHeader>
                      <CardTitle className="text-white">Registered Users</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow className="border-cyan-500/10">
                            <TableHead className="text-slate-400">Name</TableHead>
                            <TableHead className="text-slate-400">Email</TableHead>
                            <TableHead className="text-slate-400">Phone</TableHead>
                            <TableHead className="text-slate-400">Role</TableHead>
                            <TableHead className="text-slate-400">Joined</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {users.map((user) => (
                            <TableRow key={user.id} className="border-cyan-500/10">
                              <TableCell className="text-white font-medium">{user.name}</TableCell>
                              <TableCell className="text-slate-300">{user.email}</TableCell>
                              <TableCell className="text-slate-300">{user.phone}</TableCell>
                              <TableCell>
                                <Badge className={user.role === "admin" ? "bg-purple-500/20 text-purple-400" : "bg-cyan-500/20 text-cyan-400"}>
                                  {user.role}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-slate-400 text-sm">
                                {new Date(user.created_at).toLocaleDateString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="settings">
                  <Card className="bg-slate-800/50 border-cyan-500/10">
                    <CardHeader>
                      <CardTitle className="text-white">Admin Settings</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 max-w-xl">
                      <h4 className="text-white font-medium">Change Admin Password</h4>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-slate-300">Current Password</Label>
                          <Input
                            type="password"
                            value={passwordForm.current_password}
                            onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                            className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">New Password</Label>
                          <Input
                            type="password"
                            value={passwordForm.new_password}
                            onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                            className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Confirm New Password</Label>
                          <Input
                            type="password"
                            value={passwordForm.confirm_password}
                            onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                            className="mt-1 bg-slate-800/50 border-cyan-500/20 text-white"
                          />
                        </div>
                      </div>
                      {passwordError && <p className="text-sm text-red-400">{passwordError}</p>}
                      {passwordMessage && <p className="text-sm text-green-400">{passwordMessage}</p>}
                      <Button
                        onClick={changeAdminPassword}
                        disabled={passwordLoading}
                        className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full"
                      >
                        {passwordLoading ? "Updating..." : "Update Password"}
                      </Button>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const SmileRevealScreen = ({ onUnlock }) => {
  const [sliderValue, setSliderValue] = useState(0);
  const [sparkleActive, setSparkleActive] = useState(false);
  const unlockTriggered = useRef(false);

  const handleSlide = (e) => {
    const nextValue = Number(e.target.value);
    setSliderValue(nextValue);
    setSparkleActive(nextValue >= 100);

    if (nextValue >= 95 && !unlockTriggered.current) {
      unlockTriggered.current = true;
      setSliderValue(100);
      setSparkleActive(true);
      window.setTimeout(() => {
        onUnlock();
      }, 550);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.55, ease: "easeInOut" }}
      className="fixed inset-0 z-[100] smile-reveal-screen flex items-center justify-center px-6"
    >
      <div className="relative w-full max-w-xl">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="smile-reveal-card rounded-3xl border border-cyan-200/70 p-8 md:p-10"
        >
          <p className="text-slate-700 text-center text-sm md:text-base mb-6">
            Slide to reveal your smile
          </p>
          <div className="relative">
            <input
              type="range"
              min="0"
              max="100"
              value={sliderValue}
              onChange={handleSlide}
              className="smile-reveal-slider w-full"
              aria-label="Slide to reveal your smile"
            />
            <div
              className="absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 pointer-events-none transition-all duration-200"
              style={{ width: `${sliderValue}%` }}
            />
          </div>
        </motion.div>

        {sparkleActive && (
          <div className="pointer-events-none absolute inset-0 sparkle-layer">
            <span className="sparkle s1" />
            <span className="sparkle s2" />
            <span className="sparkle s3" />
            <span className="sparkle s4" />
            <span className="sparkle s5" />
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Home / main site (landing + modals)
function MainSite() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isMyAppointmentsOpen, setIsMyAppointmentsOpen] = useState(false);
  const [isClinicsOpen, setIsClinicsOpen] = useState(false);
  const [isDoctorsOpen, setIsDoctorsOpen] = useState(false);
  const [isDeveloperOpen, setIsDeveloperOpen] = useState(false);
  const promptsInitialized = useRef(false);
  const isAdminSession = () => getRoleFromStoredToken() === "admin";
  const openBookingModal = () => {
    if (isAdminSession()) return;
    const hasToken = !!localStorage.getItem("token");
    if (!hasToken) {
      setIsModalOpen(false);
      setIsAuthOpen(true);
      return;
    }
    setIsAuthOpen(false);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (promptsInitialized.current) return;
    promptsInitialized.current = true;

    const hasToken = !!localStorage.getItem("token");
    const tokenRole = getRoleFromStoredToken();
    const timer = setTimeout(() => {
      if (hasToken) {
        if (tokenRole === "admin") {
          return;
        }
        setIsModalOpen(true);
      } else {
        setIsAuthOpen(true);
      }
    }, 6000);

    return () => clearTimeout(timer);
  }, []);

  const handleAuthSuccess = () => {
    setTimeout(() => {
      if (isAdminSession()) return;
      setIsModalOpen(true);
    }, 800);
  };

  return (
    <div className="App min-h-screen relative">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <BallpitBackground />
        <div className="relative z-10">
          <Header
            onHomeClick={() => setIsDoctorsOpen(false)}
            onBookClick={openBookingModal}
            onAuthClick={() => setIsAuthOpen(true)}
            onAdminClick={() => setIsAdminOpen(true)}
            onMyAppointmentsClick={() => setIsMyAppointmentsOpen(true)}
            onClinicsClick={() => setIsClinicsOpen(true)}
            onDoctorsClick={() => setIsDoctorsOpen(true)}
          />

          {isDoctorsOpen ? (
            <DoctorsDirectoryPage onBookClick={openBookingModal} />
          ) : (
            <>
              <HeroSection onBookClick={openBookingModal} />
              <AboutSection />
              <DentalProblemsSection />
              <PlansSection />
              <CTASection onBookClick={openBookingModal} />
              <ContactCTASection />
            </>
          )}

          <Footer onDeveloperClick={() => setIsDeveloperOpen(true)} />
          <AppointmentModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
          <AuthModal
            isOpen={isAuthOpen}
            onClose={() => setIsAuthOpen(false)}
            onAuthSuccess={handleAuthSuccess}
          />
          <MyAppointmentsModal isOpen={isMyAppointmentsOpen} onClose={() => setIsMyAppointmentsOpen(false)} />
          <ClinicsModal isOpen={isClinicsOpen} onClose={() => setIsClinicsOpen(false)} />
          <DeveloperModal isOpen={isDeveloperOpen} onClose={() => setIsDeveloperOpen(false)} />
          <AdminDashboard isOpen={isAdminOpen} onClose={() => setIsAdminOpen(false)} />
        </div>
      </motion.div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Routes>
          <Route path="/services/disease/:slug" element={<DiseaseDetailPage />} />
          <Route path="*" element={<MainSite />} />
        </Routes>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
